import { useEffect, useRef, useState } from 'react';

import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

import { ReactComponent as CloseIcon } from '../../../../icons/close.svg';
import { ReactComponent as LocationIcon } from '../../../../icons/location.svg';
import { ReactComponent as SearchIcon } from '../../../../icons/search.svg';
import { getFirestoreValue } from '../../../../utils/firestore/tools';
import { Input } from '../../../common/input/Input';

import './shop-location-picker.scss';

const LIBRARIES = ['places'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '240px', border: 'none', display: 'block' };

const reverseGeocode = (lat, lng) =>
  new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      resolve(status === 'OK' && results[0] ? results[0].formatted_address : null);
    });
  });

// Inner component — only rendered once we have a valid API key, so useLoadScript always gets a real key
const ShopLocationMap = ({ googleApiKey, lat, lng, onChange }) => {
  const [center, setCenter] = useState({ lat: lat ?? -25.8853, lng: lng ?? 28.1037 });
  const [address, setAddress] = useState('');
  const inputRef = useRef(null);

  const { isLoaded } = useLoadScript({ googleMapsApiKey: googleApiKey, libraries: LIBRARIES });

  useEffect(() => {
    if (lat != null && lng != null) setCenter({ lat, lng });
  }, [lat, lng]);

  // Resolve address for the saved location on load
  useEffect(() => {
    if (!isLoaded || lat == null || lng == null) return;
    reverseGeocode(lat, lng).then((result) => {
      if (result) setAddress(result);
    });
  }, [isLoaded, lat, lng]);

  const applyLocation = async (newLat, newLng, resolvedAddress) => {
    setCenter({ lat: newLat, lng: newLng });
    const addr = resolvedAddress ?? (await reverseGeocode(newLat, newLng)) ?? '';
    setAddress(addr);
    onChange({ lat: newLat, lng: newLng });
  };

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google) return;
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['geocode', 'establishment']
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      const newLat = place.geometry.location.lat();
      const newLng = place.geometry.location.lng();
      if (inputRef.current) inputRef.current.value = '';
      applyLocation(newLat, newLng, place.formatted_address);
    });
  };

  const handleMarkerDrag = (e) => applyLocation(e.latLng.lat(), e.latLng.lng());
  const handleMapClick = (e) => applyLocation(e.latLng.lat(), e.latLng.lng());

  if (!isLoaded) return <div className="shop-location-picker__loading">Loading map...</div>;

  const markerPosition = lat != null && lng != null ? { lat, lng } : center;

  return (
    <>
      <Input
        ref={inputRef}
        type="text"
        placeholder="Search for shop address"
        icon={SearchIcon}
        clearIcon={CloseIcon}
        onChange={() => {}}
        onFocus={initAutocomplete}
      />

      <div className="shop-location-picker__map-card">
        <GoogleMap
          center={center}
          zoom={14}
          mapContainerStyle={MAP_CONTAINER_STYLE}
          onClick={handleMapClick}
        >
          <Marker position={markerPosition} draggable onDragEnd={handleMarkerDrag} />
        </GoogleMap>
      </div>

      {lat != null && lng != null && (
        <div className="shop-location-picker__selected">
          <div className="shop-location-picker__selected-icon">
            <LocationIcon width={16} height={16} />
          </div>
          <div className="shop-location-picker__selected-info">
            {address && <span className="shop-location-picker__address">{address}</span>}
            <span className="shop-location-picker__coords">
              {lat.toFixed(6)}, {lng.toFixed(6)}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export const ShopLocationPicker = ({ lat, lng, onChange }) => {
  const [googleApiKey, setGoogleApiKey] = useState('');

  useEffect(() => {
    getFirestoreValue('google_api_key').then(setGoogleApiKey);
  }, []);

  return (
    <div className="shop-location-picker">
      {googleApiKey ? (
        <ShopLocationMap
          googleApiKey={googleApiKey}
          lat={lat}
          lng={lng}
          onChange={onChange}
        />
      ) : (
        <div className="shop-location-picker__loading">Loading map...</div>
      )}
    </div>
  );
};
